export type Point = { x: number; y: number };
type Edge = { v1: Point; v2: Point };
type Rectangle = {
  topLeft: Point;
  topRight: Point;
  bottomLeft: Point;
  bottomRight: Point;
};
export type Triangle = { A: Point; B: Point; C: Point };

export const getMousePosition = function (mouseEvent: MouseEvent): Point {
  let pos: Point = { x: 0, y: 0 };
  if (mouseEvent.pageX || mouseEvent.pageY) {
    pos = { x: mouseEvent.pageX, y: mouseEvent.pageY };
  } else if (mouseEvent.clientX || mouseEvent.clientY) {
    pos = {
      x: mouseEvent.clientX + document.body.scrollLeft + document.documentElement.scrollLeft,
      y: mouseEvent.clientY + document.body.scrollTop + document.documentElement.scrollTop,
    };
  }
  return pos;
};

export const checkAim = (
  menuElem: Element | null,
  currentPosition: Point,
  previousPosition: Point
): boolean => {
  if (!menuElem) return false;
  const menuBox = getRectangle(menuElem);
  const triangle = getTriangleZone(menuBox, previousPosition, "right");
  return isInsideTriangle(triangle, currentPosition);
};

export const getRectangle = function (elem: Element | null): Rectangle {
  if (!elem) {
    return {
      topLeft: { x: 0, y: 0 },
      topRight: { x: 0, y: 0 },
      bottomLeft: { x: 0, y: 0 },
      bottomRight: { x: 0, y: 0 },
    };
  }
  const boundingBox = elem.getBoundingClientRect();
  const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;
  const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
  return {
    topLeft: {
      x: boundingBox.left + scrollLeft,
      y: boundingBox.top + scrollTop,
    },
    topRight: {
      x: boundingBox.left + scrollLeft + boundingBox.width,
      y: boundingBox.top + scrollTop,
    },
    bottomLeft: {
      x: boundingBox.left + scrollLeft,
      y: boundingBox.top + scrollTop + boundingBox.height,
    },
    bottomRight: {
      x: boundingBox.left + scrollLeft + boundingBox.width,
      y: boundingBox.top + scrollTop + boundingBox.height,
    },
  };
};

export const getTriangleZone = function (
  menuBox: Rectangle,
  point: Point,
  direction = "right"
): Triangle {
  if (direction === "right") {
    const height = menuBox.bottomRight.y - menuBox.topRight.y;
    return {
      A: {
        x: point.x,
        y: point.y,
      },
      B: {
        x: menuBox.topRight.x,
        y: Math.min(0, menuBox.topRight.y - height),
      },
      C: {
        x: menuBox.bottomRight.x,
        y: Math.max(window.innerHeight + height, menuBox.bottomRight.y + height),
      },
    };
  } else if (direction === "down") {
    return {
      A: point,
      B: menuBox.topRight,
      C: menuBox.bottomRight,
    };
  }
  return {
    A: point,
    B: menuBox.topLeft,
    C: menuBox.bottomLeft,
  };
};

const checkSameSide = (edge: Edge, point1: Point, point2: Point): boolean => {
  return getPolarity(edge, point1) === getPolarity(edge, point2);
};

export const isInsideTriangle = (triangle: Triangle, point: Point): boolean => {
  const { A, B, C } = triangle;
  return (
    checkSameSide(toEdge(A, B), C, point) &&
    checkSameSide(toEdge(A, C), B, point) &&
    checkSameSide(toEdge(B, C), A, point)
  );
};

const getPolarity = (edge: Edge, point: Point) => {
  const vectorA = toVector(edge.v1, edge.v2);
  const vectorB = toVector(edge.v1, point);
  const scalar = vectorA.x * vectorB.y - vectorA.y * vectorB.x;

  return scalar >= 0 ? 1 : -1;
};

const toEdge = (pointA: Point, pointB: Point): Edge => {
  return {
    v1: { ...pointA },
    v2: { ...pointB },
  };
};

const toVector = (pointA: Point, pointB: Point) => {
  return {
    x: pointB.x - pointA.x,
    y: pointB.y - pointA.y,
  };
};
